import { useCallback, useEffect, useState } from "react";
import { View } from "react-native";

import {
  createPlayer,
  deleteSession,
  fetchPlayers,
  setParticipants,
  updateSession,
  type Participant,
  type Player,
  type Session,
} from "./api";
import { parsePlayedOn } from "./played-on";
import { formatPlayedAt, toDateInput } from "./played-date";
import { attendanceKey, formatAttendance, winnerOf } from "./attendance";
import {
  ActionRow,
  Button,
  Card,
  CardHead,
  Chip,
  ChipRow,
  ErrorText,
  Field,
  Label,
  Meta,
  RatingPicker,
  Title,
} from "./ui";

/**
 * One recorded play, and the means to correct it (WI-039, WI-043, WI-045).
 *
 * Logging is one tap by design (ADR-0007), so most plays arrive with no
 * rating, no notes, today's date and nobody at the table. This is the only
 * place to add them afterwards, and an event-first model (ADR-0006) keeps
 * every play forever, so removing a mistaken one is part of the design.
 *
 * The editor is inline rather than a route of its own: the thing being changed
 * is one row, and a screen would put a navigation stack between somebody and
 * a typo.
 */

type Mode = "view" | "edit" | "saving" | "confirm" | "deleting";

export function SessionRow({
  session,
  onSaved,
  onDeleted,
}: {
  session: Session;
  onSaved: (next: Session) => void;
  onDeleted: (id: string) => void;
}) {
  const [mode, setMode] = useState<Mode>("view");
  const [error, setError] = useState<string | null>(null);

  const [rating, setRating] = useState<number | null>(session.rating ?? null);
  const [notes, setNotes] = useState(session.notes ?? "");
  const [playedOn, setPlayedOn] = useState(toDateInput(session.playedAt));

  const [roster, setRoster] = useState<Player[]>([]);
  const [chosen, setChosen] = useState<Set<string>>(new Set());
  const [winner, setWinner] = useState<string | null>(null);
  const [newName, setNewName] = useState("");

  /** Fetched when the row opens; a hundred rows must not be a hundred requests. */
  useEffect(() => {
    if (mode !== "edit") return;
    const controller = new AbortController();

    fetchPlayers(controller.signal)
      .then((found) => {
        if (!controller.signal.aborted) setRoster(found);
      })
      .catch(() => {
        // A roster that will not load must not block the rest of the edit, and
        // cannot lose anybody: `chosen` came from the session, so an unchanged
        // set is never written back. See `save`.
      });

    return () => controller.abort();
  }, [mode]);

  const open = useCallback(() => {
    // From what is stored, not from whatever was typed and abandoned last time.
    setRating(session.rating ?? null);
    setNotes(session.notes ?? "");
    setPlayedOn(toDateInput(session.playedAt));
    setChosen(new Set(session.participants.map((p) => p.playerId)));
    setWinner(session.participants.find((p) => p.isWinner === true)?.playerId ?? null);
    setError(null);
    setMode("edit");
  }, [session]);

  const toggle = useCallback((id: string) => {
    setChosen((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
    // Somebody who is no longer at the table cannot have won it.
    setWinner((current) => (current === id ? null : current));
  }, []);

  const addPlayer = useCallback(() => {
    const name = newName.trim();
    if (name === "") return;
    setNewName("");

    createPlayer(name)
      .then((player) => {
        setRoster((current) => [player, ...current]);
        setChosen((current) => new Set(current).add(player.id));
      })
      .catch((cause: Error) => setError(cause.message));
  }, [newName]);

  const save = useCallback(() => {
    const played = parsePlayedOn(playedOn);
    if (played === "invalid") {
      // Guarded before the mode changes. Going to "saving" first leaves the
      // form wedged on a spinner: refusing and failing must not look alike.
      setError("That date did not make sense. Use YYYY-MM-DD.");
      return;
    }

    const trimmed = notes.trim();

    // Absent means leave alone, null clears. A blank date therefore changes
    // nothing rather than quietly moving the play to today.
    const patch = {
      rating,
      notes: trimmed === "" ? null : trimmed,
      ...(played === null ? {} : { playedAt: played.toISOString() }),
    };

    const wanted = [...chosen].map((playerId) => ({
      playerId,
      isWinner: winner === null ? null : playerId === winner,
    }));

    /**
     * Only written when it differs. `setParticipants` replaces the whole set,
     * so calling it needlessly is a delete and re-insert of rows nobody asked
     * to change -- and it makes the failed-roster case safe, since `chosen` is
     * then still exactly what the session had.
     */
    const attendanceChanged = attendanceKey(wanted) !== attendanceKey(session.participants);

    setMode("saving");
    setError(null);

    updateSession(session.id, patch)
      .then(async () => {
        let participants: Participant[] = session.participants;

        if (attendanceChanged) {
          // A different resource, so a second call. A failure here must not
          // lose the edit that already succeeded.
          participants = await setParticipants(session.id, wanted).catch(() => {
            setError("The play was saved, but the players could not be updated.");
            return session.participants;
          });
        }

        setMode("view");
        onSaved({ ...session, ...patch, participants });
      })
      .catch((cause: Error) => {
        // Back to the editor with the text still in it: losing what somebody
        // typed because the network blinked is worse than the failure.
        setMode("edit");
        setError(cause.message);
      });
  }, [session, rating, notes, playedOn, chosen, winner, onSaved]);

  const remove = useCallback(() => {
    setMode("deleting");
    setError(null);

    deleteSession(session.id)
      .then(() => onDeleted(session.id))
      .catch((cause: Error) => {
        setMode("edit");
        setError(cause.message);
      });
  }, [session.id, onDeleted]);

  if (mode === "view") {
    const won = winnerOf(session.participants);
    return (
      <Card>
        <CardHead>
          <Title>{session.title}</Title>
          <Meta>{formatPlayedAt(session.playedAt)}</Meta>
        </CardHead>

        {session.participants.length > 0 && (
          <Meta>with {formatAttendance(session.participants)}</Meta>
        )}
        {won !== null && <Meta>🏆 {won}</Meta>}
        {session.notes ? <Meta>{session.notes}</Meta> : null}

        <CardHead>
          <Meta>{session.rating == null ? "No rating" : `Rated ${session.rating}/10`}</Meta>
          <Button label="Edit" onPress={open} />
        </CardHead>
      </Card>
    );
  }

  if (mode === "confirm" || mode === "deleting") {
    // Two taps, inline. `Alert.alert` is the obvious choice and the wrong one:
    // react-native-web does not implement it, so on the surface this project
    // deploys the dialog never appears and the button does nothing.
    return (
      <Card>
        <Title>Delete this play of {session.title}?</Title>
        <Meta>It will not be counted anywhere again.</Meta>
        <ErrorText>{error}</ErrorText>
        <ActionRow destructive={
          <Button
            label={mode === "deleting" ? "Deleting…" : "Delete"}
            variant="danger"
            onPress={remove}
            disabled={mode === "deleting"}
          />
        }>
          <Button label="Keep it" onPress={() => setMode("edit")} disabled={mode === "deleting"} />
        </ActionRow>
      </Card>
    );
  }

  const busy = mode === "saving";
  const playing = roster.filter((p) => chosen.has(p.id));

  return (
    <Card>
      <Title>{session.title}</Title>
      <ErrorText>{error}</ErrorText>

      <Label>Rating</Label>
      <RatingPicker value={rating} onChange={setRating} />

      <Label>Played on</Label>
      <Field
        value={playedOn}
        onChangeText={setPlayedOn}
        placeholder="YYYY-MM-DD"
        autoCapitalize="none"
        autoCorrect={false}
        inputMode="numeric"
      />

      <Label>Who played</Label>
      {roster.length === 0 && <Meta>Nobody on your roster yet.</Meta>}
      <ChipRow>
        {roster.map((p) => (
          <Chip
            key={p.id}
            label={p.displayName}
            selected={chosen.has(p.id)}
            onPress={() => toggle(p.id)}
          />
        ))}
      </ChipRow>

      {chosen.size > 0 && (
        <>
          <Label>Who won</Label>
          <ChipRow>
            {playing.map((p) => (
              <Chip
                key={p.id}
                label={winner === p.id ? `🏆 ${p.displayName}` : p.displayName}
                selected={winner === p.id}
                tone="win"
                onPress={() => setWinner(winner === p.id ? null : p.id)}
              />
            ))}
            <Chip
              label="Nobody won"
              selected={winner === null}
              onPress={() => setWinner(null)}
            />
          </ChipRow>
        </>
      )}

      <View style={{ flexDirection: "row", gap: 8, alignItems: "center" }}>
        <Field
          value={newName}
          onChangeText={setNewName}
          onSubmitEditing={addPlayer}
          placeholder="Add someone"
          returnKeyType="done"
          style={{ flex: 1 }}
        />
        <Button label="Add" onPress={addPlayer} />
      </View>

      <Label>Notes</Label>
      <Field
        value={notes}
        onChangeText={setNotes}
        placeholder="Anything worth remembering"
        multiline
      />

      <ActionRow destructive={
        <Button label="Delete" variant="danger" onPress={() => setMode("confirm")} disabled={busy} />
      }>
        <Button label="Save changes" variant="primary" onPress={save} busy={busy} />
        <Button label="Cancel" onPress={() => setMode("view")} disabled={busy} />
      </ActionRow>
    </Card>
  );
}
